import { Eye, Download, FileOutput, Users, Clock, TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { Link } from 'react-router-dom'
import { cn } from '@/lib/utils'
import type { TemplateAnalytics } from '@/lib/api/types'

interface TemplateAnalyticsCardProps {
  data: TemplateAnalytics
  loading?: boolean
}

export function TemplateAnalyticsCard({ data, loading }: TemplateAnalyticsCardProps) {
  if (loading) {
    return (
      <div className="bg-white dark:bg-[#292929] rounded-xl border border-[#e5e5e5] dark:border-[#3d3d3d] p-5 shadow-sm animate-pulse">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700" />
          <div className="flex-1">
            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded mb-2" />
            <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          ))}
        </div>
      </div>
    )
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    return `${(ms / 60000).toFixed(1)}m`
  }

  const stats = [
    {
      label: 'Views',
      value: data.viewCount,
      icon: Eye,
      color: 'text-blue-500',
    },
    {
      label: 'Downloads',
      value: data.downloadCount,
      icon: Download,
      color: 'text-emerald-500',
    },
    {
      label: 'Generated',
      value: data.generationCount,
      icon: FileOutput,
      color: 'text-violet-500',
    },
    {
      label: 'Unique Users',
      value: data.uniqueUsers,
      icon: Users,
      color: 'text-amber-500',
    },
    {
      label: 'Avg Duration',
      value: formatDuration(data.averageDurationMs),
      icon: Clock,
      color: 'text-pink-500',
    },
    {
      label: 'Completion',
      value: `${data.fieldCompletionRate.toFixed(0)}%`,
      icon: data.fieldCompletionRate >= 50 ? TrendingUp : TrendingDown,
      color: data.fieldCompletionRate >= 50 ? 'text-green-500' : 'text-red-500',
    },
  ]

  return (
    <Link
      to={`/templates/${data.templateId}/analytics`}
      className="block bg-white dark:bg-[#292929] rounded-xl border border-[#e5e5e5] dark:border-[#3d3d3d] p-5 shadow-sm hover:shadow-md hover:border-blue-200 dark:hover:border-blue-800 transition-all group"
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        {data.templateThumbnail ? (
          <img
            src={data.templateThumbnail}
            alt={data.templateName}
            className="w-12 h-12 rounded-lg object-cover"
          />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
            <FileOutput className="h-6 w-6 text-white" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
            {data.templateName}
          </h3>
          <div className="flex items-center gap-2">
            <span
              className={cn(
                'text-xs font-medium px-2 py-0.5 rounded-full',
                data.trend > 0 && 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                data.trend < 0 && 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
                data.trend === 0 && 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
              )}
            >
              {data.trend > 0 && <TrendingUp className="h-3 w-3 inline mr-1" />}
              {data.trend < 0 && <TrendingDown className="h-3 w-3 inline mr-1" />}
              {data.trend === 0 && <Minus className="h-3 w-3 inline mr-1" />}
              {data.trend > 0 ? '+' : ''}{data.trend.toFixed(1)}%
            </span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2">
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="p-2 rounded-lg bg-gray-50 dark:bg-gray-800/50"
          >
            <div className="flex items-center gap-1.5 mb-1">
              <stat.icon className={cn('h-3.5 w-3.5', stat.color)} />
              <span className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</span>
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">
              {typeof stat.value === 'number' ? stat.value.toLocaleString() : stat.value}
            </p>
          </div>
        ))}
      </div>
    </Link>
  )
}
